// import {MouseEvent} from 'react'
import cn from 'clsx'
import {parseISO} from 'date-fns'
import {Avatar, Space} from 'antd'
import styles from './RequestsList.module.scss'
// import useStore from 'base/store'
import {PROJECT_CATEGORY_NAME, ProjectCategory} from 'modules/project'
import {
  // ManagerAssignForm,
  REQUEST_STATUS_NAME,
  REQUEST_APPROVED_STATUSES,
  RequestStatus,
  RequestTagType,
} from 'modules/request'
import { RequestStatus as VariationRequestStatus } from 'modules/request/types.variation'
import {
  REQUEST_STATUS_NAME as VARIATION_REQUEST_STATUS_NAME,
  REQUEST_APPROVED_STATUSES as VARIATION_REQUEST_APPROVED_STATUSES,
} from 'modules/request/constants.variation'
import {
  DRAWDOWN_REQUEST_STATUS_NAME,
  DrawdownRequestStatus
} from 'modules/request/constants.drawdown'

import {/*getShortName,*/ ProfileRole, User} from 'modules/users'
import {useOpen} from 'common/hooks'
import {
  Box,
  Block,
  Tag,
  // Button,
  Text,
  UserAvatar,
  // Modal,
  AttachmentLinkIcon,
} from 'common/components'
import {formatDate, formatMoney, cutText} from 'common/helpers'
import {RequestsItemPeriod} from '.'

interface RequestsListProps {
  items: any[]
  loading: boolean
  tag?: RequestTagType
  urlPrefix?: string
}

interface RequestProps {
  // role: ProfileRole
  request: any
  isRequestsLoading: boolean
  tag?: RequestTagType
  urlPrefix?: string
}

interface UsersProps {
  role?: ProfileRole
  requestStatus?: RequestStatus
  isRequestsLoading: boolean
  borrower?: User | any
  manager?: User
  onManagerAssignOpen: any
}

export default function RequestsList({urlPrefix, tag, items, loading}: RequestsListProps) {
  return (
    <ul className={styles.root}>
      {items.map((item, index:number) => (
        <Request
          key={index}
          // role={role}
          request={item}
          isRequestsLoading={loading}
          tag={tag}
          urlPrefix={urlPrefix}
        />
      ))}
    </ul>
  )
}

const Request = ({urlPrefix, tag, request, isRequestsLoading}: RequestProps) => {
  const [
    // isManagerAssignOpen,
    handleManagerAssignOpen,
    // handleManagerAssignClose,
  ] = useOpen(false)

  const {project} = request
  const isDeclined = request.status === 'declined'
  const isEstimated = !request.loanValue
  const loanText = isDeclined
    ? 'Declined loan'
    : isEstimated
    ? 'Requested loan'
    : 'Loan'

  return (
    <>
      <Block as="li" to={
        (tag === 'drawdown') ? `/projects/${project.uuid}/drawdowns` :
        `/requests/${!urlPrefix ? '' : (urlPrefix + '/')}${request.uuid}`
      }>
        <div className={styles.section}>
          <div className={styles.header}>
            <div className={styles.block}>
              <div className={styles.dateBlock}>
                <p className={styles.date}>{formatDate(request.createdAt)}</p>
              </div>
              <p className={styles.title}>{cutText(project.title)}</p>
            </div>
            <div className={styles['header-right']}>
              <Avatar size={96} src={project.image} alt={project.title} />
              { !!tag && (tag !== 'loan') &&
                <div className={styles['header-tag']}>
                  <Tag color="danger" variant="round">{tag}</Tag>
                </div>
              }
            </div>
          </div>
          <div className={styles.texts}>
            <Space size={6} split="•">
              {project.categories?.map(
                (category: ProjectCategory) => PROJECT_CATEGORY_NAME[category]
              )}
            </Space>
            <Text mt={6}>
              { [project?.address || '', project?.region || ''].filter((x:string) => !!x).join(', ')}
            </Text>
          </div>
          <Users
            // role={role}
            // requestStatus={request.status}
            isRequestsLoading={isRequestsLoading}
            borrower={project.borrower}
            manager={project.manager}
            onManagerAssignOpen={handleManagerAssignOpen}
          />
          { (tag === 'loan') && 
            <RequestsItemPeriod
              estimated={isEstimated}
              from={parseISO(request.openDate)}
              to={parseISO(request.maturityDate)}
              maxLvr={request.lvrMax}
            />
          }
          { ('drawdown' === tag) && 
            <AmountInfo info={{
              'Approved loan amount': request?.approvedAmount ?? 0,
              'Available drawdown': request?.project?.availableWithdrawal ?? 0,
            }} />
          }
          { ('default' === tag) && 
            <DefaultInfo startDate={request?.startDate || ''} stopDate={request?.endDate || ''}
              rate={request?.interest ?? request?.project?.defaultInterestRate ?? 15}
            />
          }
          { ('variation' === tag) && 
            <>
            <Text weight="medium" size="xs" color="grayer">Variation type</Text>
            <Text weight="demiBold" size="xl" color="black">
              {request.type}
            </Text>
            </>
          }
          { ('journal' === tag) && 
            <>
            <Text weight="medium" size="xs" color="grayer">Journal type</Text>
            <Text weight="demiBold" size="xl" color="black">Journal Default Interest Credit</Text>
            </>
          }
        </div>
        <div className={cn(styles.section, styles.section_footer)}>
          <FooterTag isDeclined={isDeclined} requestStatus={request.status} tag={tag} />
        </div>
        <div style={{padding: '0 30px 30px'}}>
          <Box >
            { (!tag || tag === 'loan') &&
              <Text size="xs" color="grey" mb={2}>
                {loanText}
              </Text>
            }
            { (tag === 'drawdown') &&
              <Text size="xs" color="grey" mb={2}>
                Requested drawdown
              </Text>
            }
            { ['loan'].includes(tag) && 
              <Text
                size="3xl"
                weight="medium"
                cross={isDeclined}
                color={isEstimated ? 'grey' : 'black'}
              >
                {formatMoney(request.loanValue || request.loanValueRequested)}
              </Text>
            }
            { ['drawdown'].includes(tag) && 
              <Text
                size="3xl"
                weight="medium"
                cross={isDeclined}
                color={isEstimated ? 'grey' : 'black'}
              >
                {formatMoney(request.requestedAmount)}
              </Text>
            }
            { ['journal'].includes(tag) && 
              <Text
                size="3xl"
                weight="medium"
                cross={isDeclined}
                color={isEstimated ? 'grey' : 'black'}
              >
                {formatMoney(request.amount)}
              </Text>
            }
          </Box>
        </div>
      </Block>
      {/* <Modal
        title={`Manager ${project?.manager ? 're-' : ''}assign`}
        open={isManagerAssignOpen}
        onCancel={handleManagerAssignClose}
      >
        <ManagerAssignForm
          request={request}
          projectUuid={project.uuid}
          manager={project?.manager}
          onClose={handleManagerAssignClose}
        />
      </Modal> */}
    </>
  )
}

const isSucceeded = (tag: RequestTagType, status:any) => (
  (tag === 'variation')
  ? 
  VARIATION_REQUEST_APPROVED_STATUSES.includes(status)
  :
  REQUEST_APPROVED_STATUSES.includes(status)
)
const nameOfRequestStatus = (tag: RequestTagType, status:any) => (
  (tag === 'variation') ? VARIATION_REQUEST_STATUS_NAME[(status as VariationRequestStatus)] :
  (tag === 'drawdown') ? DRAWDOWN_REQUEST_STATUS_NAME[(status as DrawdownRequestStatus)] :
  REQUEST_STATUS_NAME[(status as RequestStatus)]
)

const FooterTag = ({isDeclined, requestStatus, tag}:{
  isDeclined: boolean;
  requestStatus: any;
  tag: RequestTagType;
}) => {
  return (
    <Tag
      icon={isDeclined ? 'closeLarge' : 'checkLarge'}
      color={
        isDeclined ? 'danger' :
        isSucceeded(tag, requestStatus) ? 'success' :
        undefined
      }
    >
    {
      nameOfRequestStatus(tag, requestStatus)
    }
    </Tag>
  )
}
const Users = ({
  // role,
  // requestStatus,
  // isRequestsLoading,
  borrower,
  manager,
  // onManagerAssignOpen,
}: UsersProps) => {
  // const handleManagerAssign = (e: MouseEvent) => {
  //   e.preventDefault()
  //   onManagerAssignOpen()
  // }

  // console.log(borrower)

  return (
    <Space className={styles.users} size={6}>
      {(borrower || manager) && (
        <Avatar.Group>
          {borrower && (
            <UserAvatar
              size={24}
              src={borrower.photo}
              alt={borrower.name}
            />
          )}
          {manager && (
            <UserAvatar size={24} src={manager.photo} alt={manager.firstName} />
          )}
        </Avatar.Group>
      )}
      {borrower && <span>{borrower.name}</span>}
      {manager ? (
        <>
          <AttachmentLinkIcon />
          <span>{manager.firstName}</span>
          {/* {role.coordinator && requestStatus === 'loans-coordinator-check' && (
            <Button
              variant="secondary"
              disabled={isRequestsLoading}
              onClick={handleManagerAssign}
            >
              re-assign
            </Button>
          )} */}
        </>
      ) : (
        null
        // role.coordinator && (
        //   <Button
        //     variant="secondary"
        //     disabled={isRequestsLoading}
        //     onClick={handleManagerAssign}
        //   >
        //     assign manager
        //   </Button>
        // )
      )}
    </Space>
  )
}

interface AmountInfoProps {
  info: Record<string, number>;
}
const AmountInfo: React.FC<AmountInfoProps> = ({info}) => {
  return (
    <>
      <div className={styles['amount-info']}>
      { Object.keys(info).map((key:string, index:number) => (
        <Text key={index} weight="medium" size="xs" color="grayer">{key}</Text>
      ))}
      </div>
      <div className={styles['amount-info']}>
      { Object.values(info).map((value:number, index:number) => (
        <Text key={index} weight="medium" size="3xl" color="black">{ formatMoney(value)}</Text>
      ))}
      </div>
    </>
  )
}

interface DefaultInfoProps {
  startDate: string;
  stopDate: string;
  rate: number;
}
const DefaultInfo: React.FC<DefaultInfoProps> = ({startDate, stopDate, rate}) => {
  return (
    <div className={styles['default-info']}>
      <div>
        <Text weight="medium" size="xs" color="grayer">Start date</Text>
        <Text weight="demiBold" size="xl" color="black">{!!startDate ? formatDate(startDate): '--'}</Text>
      </div>
      <div>
        <Text weight="medium" size="xs" color="grayer">Stop date</Text>
        <Text weight="demiBold" size="xl" color="black">{!!stopDate ? formatDate(stopDate):'--'}</Text>
      </div>
      <div>
        <Text weight="medium" size="xs" color="grayer">Default rate</Text>
        <Text weight="demiBold" size="xl" color="black">{rate}%</Text>
      </div>
    </div>
  )
}